import { Link } from "react-router-dom";
import { Menu, HelpCircle, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import logo from "@/assets/logo-allepets.png";
import { cn } from "@/lib/utils";

declare const bootstrap: any;

declare global {
  interface Window {
    __AUTH_ID__?: number | null;
    __AUTH_USER__?: {
      id?: number;
      name?: string;
      email?: string;
      role?: string;
      has_ong?: boolean;
      ong_panel_url?: string;
      avatar_url?: string | null;
      avatarUrl?: string | null;
      profile_photo_url?: string | null;
      foto_url?: string | null;
      foto?: string | null;
      avatar?: string | null;
    } | null;
  }
}

interface HeaderProps {
  activeTab?: "tutores" | "anfitrioes";
  isCompact?: boolean;
  hideAudienceNav?: boolean;
}

export function Header({
  activeTab = "tutores",
  isCompact = false,
  hideAudienceNav = false,
}: HeaderProps) {
  const isLoggedIn = !!window.__AUTH_ID__;

  const authUser = window.__AUTH_USER__;
  const userName = authUser?.name || "Minha conta";
  const userRole = authUser?.role || "tutor";
  const hasOng = !!authUser?.has_ong;
  const ongPanelUrl = authUser?.ong_panel_url || "/adocao/minha-ong";

  const rawAvatar =
    authUser?.avatar_url ||
    authUser?.avatarUrl ||
    authUser?.profile_photo_url ||
    authUser?.foto_url ||
    authUser?.foto ||
    authUser?.avatar ||
    null;

  const avatarUrl = rawAvatar
    ? rawAvatar.startsWith("http://") || rawAvatar.startsWith("https://")
      ? rawAvatar
      : rawAvatar.startsWith("/storage/")
      ? rawAvatar
      : `/storage/${rawAvatar.replace(/^\/+/, "")}`
    : null;

  const showTutorPanel =
    isLoggedIn &&
    (userRole === "tutor" ||
      userRole === "anfitriao" ||
      userRole === "admin");

  const showAnfitriaoPanel =
    isLoggedIn && (userRole === "anfitriao" || userRole === "admin");

  const showAdminPanel = isLoggedIn && userRole === "admin";

  const handleLogout = async () => {
    try {
      const token =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content") || "";

      const response = await fetch("/logout", {
        method: "POST",
        headers: {
          "X-CSRF-TOKEN": token,
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        credentials: "same-origin",
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      window.location.href = "/";
    } catch (error) {
      console.error("Erro ao sair:", error);
      alert("Não foi possível sair da conta.");
    }
  };

  const openAuthModal = () => {
    const modalEl = document.getElementById("authModal");
    if (!modalEl) {
      console.error("authModal não encontrado no HTML (Blade).");
      return;
    }

    const instance = bootstrap.Modal.getOrCreateInstance(modalEl, {
      backdrop: true,
      keyboard: true,
      focus: false,
    });

    instance.show();
  };

  return (
    <header
      className={`sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border transition-all duration-500 ${
        isCompact ? "py-2" : "py-4"
      }`}
    >
      <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 md:px-6">
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="flex-shrink-0 group">
            <img
              src={logo}
              alt="AllePets"
              className={cn(
                "transition-all duration-500",
                isCompact ? "h-10 md:h-16" : "h-12 md:h-24",
                "hover:scale-105 group-hover:drop-shadow-lg"
              )}
            />
          </Link>

          <div className="flex items-center gap-2">
            <Link
              to="/painel/chat-suporte"
              className="hidden md:flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-all duration-300 hover:scale-105"
            >
              <HelpCircle className="w-4 h-4" />
              Ajuda
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  className="flex items-center gap-2 rounded-full border-2 px-3 py-2 hover:shadow-md transition-all duration-300 hover:scale-105 btn-ripple"
                >
                  <Menu className="w-4 h-4" />

                  <div className="flex items-center gap-2">
                    {isLoggedIn && (
                      <div className="hidden md:flex flex-col items-start leading-tight">
                        <span className="text-xs font-semibold text-green-600">
                          Conectado
                        </span>
                        <span className="text-xs text-foreground max-w-[120px] truncate">
                          {userName}
                        </span>
                      </div>
                    )}

                    <div className="w-7 h-7 rounded-full bg-muted overflow-hidden flex items-center justify-center">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt={userName}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <User className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56 animate-spring-in">
                {isLoggedIn ? (
                  <>
                    <div className="px-3 py-2 border-b">
                      <div className="text-sm font-semibold">{userName}</div>
                      {authUser?.email && (
                        <div className="text-xs text-muted-foreground truncate">
                          {authUser.email}
                        </div>
                      )}
                    </div>

                    {showTutorPanel && (
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <a href="/tutor/dashboard" className="w-full">
                          Meu painel de tutor
                        </a>
                      </DropdownMenuItem>
                    )}

                    {showAnfitriaoPanel && (
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <a href="/anfitriao/home" className="w-full">
                          Meu painel de anfitrião
                        </a>
                      </DropdownMenuItem>
                    )}

                    {showAdminPanel && (
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <a href="/admin" className="w-full">
                          Meu painel administrativo
                        </a>
                      </DropdownMenuItem>
                    )}

                    {hasOng && (
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <a href={ongPanelUrl} className="w-full">
                          Painel da ONG
                        </a>
                      </DropdownMenuItem>
                    )}

                    {showTutorPanel && (
                      <DropdownMenuItem asChild className="cursor-pointer">
                        <a href="/tutor/pets" className="w-full">
                          Meus pets
                        </a>
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        handleLogout();
                      }}
                      className="font-semibold cursor-pointer"
                    >
                      Sair
                    </DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        setTimeout(() => openAuthModal(), 10);
                      }}
                      className="font-semibold cursor-pointer"
                    >
                      Entrar
                    </DropdownMenuItem>

                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        openAuthModal();
                      }}
                      className="cursor-pointer hover:bg-muted transition-colors"
                    >
                      Cadastrar-se
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {!hideAudienceNav && (
          <>
            <nav className="mt-3 flex md:hidden items-center gap-2 overflow-x-auto pb-1">
              <Link
                to="/"
                className={cn(
                  "shrink-0 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300",
                  activeTab === "tutores"
                    ? "bg-background text-forest shadow-sm border"
                    : "bg-muted text-muted-foreground"
                )}
              >
                Para tutores
              </Link>

              <Link
                to="/anfitrioes"
                className={cn(
                  "shrink-0 px-4 py-2 rounded-full text-xs font-semibold transition-all duration-300",
                  activeTab === "anfitrioes"
                    ? "bg-background text-forest shadow-sm border"
                    : "bg-muted text-muted-foreground"
                )}
              >
                Para anfitriões
              </Link>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="shrink-0 px-4 py-2 rounded-full text-xs font-semibold bg-muted text-muted-foreground"
                  >
                    Adoção ▾
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="start"
                  className="w-56 rounded-2xl p-2"
                  sideOffset={8}
                >
                  <DropdownMenuItem asChild className="cursor-pointer rounded-xl">
                    <a href="/adocao" className="w-full">
                      Adotar um pet
                    </a>
                  </DropdownMenuItem>

                  <DropdownMenuItem asChild className="cursor-pointer rounded-xl">
                    <a href="/adocao/doar" className="w-full">
                      Doar um pet
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            <nav className="hidden md:flex items-center gap-1 bg-muted rounded-full p-1 absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2">
              <Link
                to="/"
                className={cn(
                  "relative px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300",
                  activeTab === "tutores"
                    ? "bg-background text-forest shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-background/50"
                )}
              >
                Para tutores
                {activeTab === "tutores" && (
                  <span className="absolute inset-0 rounded-full bg-forest/10 animate-pulse-soft" />
                )}
              </Link>

              <Link
                to="/anfitrioes"
                className={cn(
                  "relative px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300",
                  activeTab === "anfitrioes"
                    ? "bg-background text-forest shadow-sm"
                    : "text-muted-foreground hover:text-foreground hover:bg-background/50"
                )}
              >
                Para anfitriões
                {activeTab === "anfitrioes" && (
                  <span className="absolute inset-0 rounded-full bg-forest/10 animate-pulse-soft" />
                )}
              </Link>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="relative px-6 py-2 rounded-full text-sm font-semibold transition-all duration-300 text-muted-foreground hover:text-foreground hover:bg-background/50"
                  >
                    Adoção ▾
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="center"
                  className="w-56 rounded-2xl p-2"
                  sideOffset={8}
                >
                  <DropdownMenuItem asChild className="cursor-pointer rounded-xl">
                    <a href="/adocao" className="w-full">
                      Adotar um pet
                    </a>
                  </DropdownMenuItem>

                  <DropdownMenuItem asChild className="cursor-pointer rounded-xl">
                    <a href="/adocao/doar" className="w-full">
                      Doar um pet
                    </a>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          </>
        )}
      </div>
    </header>
  );
}
